import React from "react";
import { ParticipantBadge } from "../../common/components/ParticipantBadge";
import { getParticipantColor } from "../../../utils/participantColors";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";

// --- Types ---

export interface ParticipantPanelData {
  id: string;
  name: string;
  alias?: string;
  code?: string;
  number?: number;
  image?: string;
  avatarUrl?: string;
  status?: string;
  metadata?: {
    colorId?: string;
    customColor?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

export type ParticipantDisplayMode = "photo" | "number" | "code";

export interface ParticipantsPanelProps {
  participants: ParticipantPanelData[];
  title?: string;
  mode?: ParticipantDisplayMode;
  onModeChange?: (mode: ParticipantDisplayMode) => void;
  size?: number;
  onSizeChange?: (size: number) => void;
  selectedIds?: string[];
  onSelect?: (participant: ParticipantPanelData) => void;
  onInfo?: (participant: ParticipantPanelData) => void;
  /** Called when an external draggable (e.g. transcript speaker) is dropped onto a participant */
  onDropItem?: (participant: ParticipantPanelData, data: string, sourceTimestamp?: number) => void;
  hiddenIds?: string[];
  onSettingsClick?: () => void;
  selectedColor?: string;
  modeLabels?: { photo: string; number: string; code: string };
  settingsTooltip?: string;
  modeTooltip?: string;
}

// --- SizeSlider (stepper) ---

const SizeSlider: React.FC<{
  value: number;
  onChange: (val: number) => void;
  min: number;
  max: number;
  step?: number;
  "data-testid"?: string;
}> = ({ value, onChange, min, max, step = 10, "data-testid": dataTestId }) => {
  const decrement = () => onChange(Math.max(min, value - step));
  const increment = () => onChange(Math.min(max, value + step));

  return (
    <ButtonGroup className="ml-2.5" data-testid={dataTestId}>
      <Button
        type="button"
        variant="outline"
        size="icon-xs"
        onClick={decrement}
        disabled={value <= min}
        aria-label="Decrease size"
      >
        <i className="bx bx-minus" />
      </Button>
      <Button
        type="button"
        variant="outline"
        size="icon-xs"
        onClick={increment}
        disabled={value >= max}
        aria-label="Increase size"
      >
        <i className="bx bx-plus" />
      </Button>
    </ButtonGroup>
  );
};

// --- Constants ---

const MODE_ICONS: Record<ParticipantDisplayMode, string> = {
  photo: "bx-camera",
  number: "bx-list-ul",
  code: "bx-barcode",
};

const MODE_CYCLE: ParticipantDisplayMode[] = ["photo", "number", "code"];
const DEFAULT_LABELS = { photo: "PHOTO", number: "TEXT", code: "CODE" };

// --- Component ---

export const ParticipantsPanel: React.FC<ParticipantsPanelProps> = ({
  participants,
  title = "PARTICIPANTS",
  mode = "photo",
  onModeChange,
  size = 100,
  onSizeChange,
  selectedIds = [],
  onSelect,
  onInfo,
  onDropItem,
  hiddenIds = [],
  onSettingsClick,
  selectedColor = "var(--color-accent-danger)",
  modeLabels = DEFAULT_LABELS,
  settingsTooltip = "Filter participants",
  modeTooltip = "Cycle display mode",
}) => {
  const cycleMode = () => {
    if (!onModeChange) return;
    const idx = MODE_CYCLE.indexOf(mode);
    onModeChange(MODE_CYCLE[(idx + 1) % MODE_CYCLE.length]);
  };

  const filtered = participants
    .filter((p) => p.status !== "eliminated" && !hiddenIds.includes(p.id))
    .sort((a, b) => (a.number || 0) - (b.number || 0));

  const hasHidden = hiddenIds.length > 0;
  const sizeMin = 60;
  const sizeMax = mode === "photo" ? 180 : 350;

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "4px",
        }}
      >
        <div
          style={{
            fontSize: "var(--text-xs)",
            textTransform: "uppercase",
            color: "var(--color-text-secondary)",
            fontWeight: "bold",
            letterSpacing: "1px",
            flexShrink: 0,
          }}
        >
          {title}
        </div>
        <div className="flex items-center gap-2">
          {onModeChange && (
            <Badge
              variant="outline"
              size="xs"
              className={`cursor-pointer select-none gap-1 ${mode !== "code" ? "text-[var(--color-accent-success)] border-[var(--color-accent-success)]/30" : "text-muted-foreground"}`}
              onClick={cycleMode}
              title={modeTooltip}
              data-testid="console-btn-participant-mode"
            >
              <i className={`bx ${MODE_ICONS[mode]}`} />
              {modeLabels[mode]}
            </Badge>
          )}
          {onSettingsClick && (
            <Button
              variant="outline"
              size="icon-xs"
              className={
                hasHidden
                  ? "text-[var(--color-accent-warning)] border-[var(--color-accent-warning)]/30"
                  : "text-muted-foreground"
              }
              onClick={onSettingsClick}
              title={settingsTooltip}
            >
              <i className="bx bx-cog" />
            </Button>
          )}
          {onSizeChange && (
            <SizeSlider
              data-testid="console-slider-participants"
              value={size}
              onChange={onSizeChange}
              min={sizeMin}
              max={sizeMax}
            />
          )}
        </div>
      </div>

      {/* Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            mode === "photo"
              ? `repeat(auto-fill, ${size}px)`
              : `repeat(auto-fit, minmax(min(${size}px, 100%), 1fr))`,
          gap: mode === "photo" ? 0 : "2px",
          justifyContent: mode === "photo" ? "center" : "start",
        }}
      >
        {filtered.map((p) => {
          const isSelected = selectedIds.includes(p.id);
          const pColor = getParticipantColor(p.metadata?.colorId, p.metadata?.customColor);

          if (mode === "photo") {
            return (
              <button
                key={p.id}
                data-testid={`matrix-item-${p.id}`}
                onClick={() => onSelect?.(p)}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = "copyMove";
                  e.dataTransfer.setData("text/plain", (p.alias || p.name).toUpperCase());
                }}
                onDragOver={
                  onDropItem
                    ? (e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "copy";
                      }
                    : undefined
                }
                onDrop={
                  onDropItem
                    ? (e) => {
                        e.preventDefault();
                        const speakerData = e.dataTransfer.getData("speaker-id");
                        const rawTimestamp = e.dataTransfer.getData("source-received-at");
                        if (speakerData)
                          onDropItem(
                            p,
                            speakerData,
                            rawTimestamp ? Number(rawTimestamp) : undefined,
                          );
                      }
                    : undefined
                }
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  aspectRatio: "1 / 1",
                }}
              >
                {/* ParticipantBadge as circular photo */}
                <ParticipantBadge
                  participant={{ ...p, image: p.image || p.avatarUrl }}
                  variant="photo"
                  sizePixels={size - 6}
                  isSelected={isSelected}
                  selectedColor={selectedColor}
                />

                {/* Number overlay (top-left) */}
                {p.number && (
                  <div
                    style={{
                      position: "absolute",
                      top: "2px",
                      left: "3px",
                      color: "#fff",
                      fontSize: "var(--text-md)",
                      fontWeight: 900,
                      textShadow: "0 2px 4px rgba(0,0,0,1)",
                      zIndex: 3,
                      pointerEvents: "none",
                      lineHeight: 1,
                    }}
                  >
                    {p.number}
                  </div>
                )}

                {/* Name label (bottom) */}
                <Badge
                  size="xs"
                  className="absolute bottom-0.5 left-1/2 -translate-x-1/2 z-[4] uppercase max-w-[95%] pointer-events-none border border-black/30 shadow-md"
                  style={{
                    background: pColor.bg,
                    color: pColor.isDark ? "#fff" : "#000",
                  }}
                >
                  {p.alias || p.name}
                </Badge>

                {/* Info icon (top-right) */}
                {onInfo && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      onInfo(p);
                    }}
                    style={{
                      position: "absolute",
                      top: "2px",
                      right: "2px",
                      zIndex: 5,
                      cursor: "pointer",
                      opacity: 0.3,
                      transition: "all 0.2s",
                      color: "rgba(255,255,255,0.8)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = "1";
                      e.currentTarget.style.transform = "scale(1.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = "0.3";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.5))" }}
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                  </div>
                )}
              </button>
            );
          }

          // Number / Code modes → pill variant
          const label =
            mode === "code" ? (p.code || p.alias || p.name).toUpperCase() : p.alias || p.name;

          return (
            <button
              key={p.id}
              data-testid={`matrix-item-${p.id}`}
              onClick={() => onSelect?.(p)}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = "copyMove";
                e.dataTransfer.setData("text/plain", label.toUpperCase());
              }}
              onDragOver={
                onDropItem
                  ? (e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "copy";
                    }
                  : undefined
              }
              onDrop={
                onDropItem
                  ? (e) => {
                      e.preventDefault();
                      const speakerData = e.dataTransfer.getData("speaker-id");
                      const rawTimestamp = e.dataTransfer.getData("source-received-at");
                      if (speakerData)
                        onDropItem(p, speakerData, rawTimestamp ? Number(rawTimestamp) : undefined);
                    }
                  : undefined
              }
              style={{
                background: isSelected ? pColor.bg : `${pColor.bg}15`,
                color: isSelected ? (pColor.isDark ? "#fff" : "#000") : "var(--color-text-primary)",
                border: isSelected ? `2px solid ${pColor.border}` : `1px solid ${pColor.border}`,
                borderRadius: "6px",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                aspectRatio: "3 / 1",
                boxShadow: isSelected ? `0 0 15px ${pColor.bg}80` : "none",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  fontWeight: 900,
                  fontSize: mode === "code" ? "var(--text-md)" : "var(--text-base)",
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: "95%",
                  textAlign: "center",
                  letterSpacing: "0.5px",
                }}
              >
                {label}
              </div>
              {mode === "number" && p.number && (
                <div
                  style={{
                    fontSize: "var(--text-2xs)",
                    opacity: 0.7,
                    fontWeight: "bold",
                    marginTop: "-1px",
                  }}
                >
                  {p.number}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
